/**
 * Contract types for the photo-moderation reviewer tool's endpoints
 * (queue / decide), plus the rejection-reason enum.
 *
 * These are the FE-side source of truth until `@owner/olympus-client` is
 * regenerated with the reviewer endpoints (GUE-3888), after which the request /
 * response shapes are replaced by the generated client types.
 *
 * Storage model: guest photos live in the shared public CDN bucket (written via
 * `ImageService.upsertImage`) — `original` is a public URL, not presigned.
 * Aligned with the GUE-3816 entity. `status` mirrors GUE-3816's enum; the
 * reward arm there is `reward300 | goodwill`.
 */

/** Single-source-of-truth Unleash flag name for the photo reviewer tool. */
export const GUEST_PHOTO_REVIEWER_FLAG = 'hermes.guest-photo-reviewer'

/**
 * Submission lifecycle status — mirrors GUE-3816's `GuestPhotoSubmissionStatus`.
 * The reviewer queue only surfaces `pending`; `accepted` / `rejected` are the
 * terminal states a decision writes.
 */
export enum GuestPhotoSubmissionStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

/** Reviewer rejection reasons — Postgres-native enum, 8 v1 values (RQ7). */
export enum GuestPhotoRejectionReason {
  QualityBlurry = 'quality_blurry',
  QualityLighting = 'quality_lighting',
  QualityPartial = 'quality_partial',
  ContentWrongItem = 'content_wrong_item',
  ContentInappropriate = 'content_inappropriate',
  ContentFraud = 'content_fraud',
  ContentPii = 'content_pii',
  Other = 'other',
}

/** A single pending submission as shown in the queue + detail views. */
export interface ReviewSubmissionSummary {
  submissionId: string
  userId: string
  userEmail: string
  orderId: string
  orderItemId: string
  /** Public CDN URL of the uploaded photo (`GuestPhotoImages.original`). */
  originalUrl: string
  submittedAt: string
  itemDescription: string
  modifiers: string[]
}

/** An item with its 1-2 pending submissions, grouped under a brand. */
export interface ReviewQueueItem {
  menuItemId: string
  menuItemName: string
  locationId: string
  locationName: string
  pendingSubmissions: ReviewSubmissionSummary[]
}

/** A brand bucket in the queue, sorted by oldest-pending age. */
export interface ReviewQueueBrand {
  brandId: string
  brandName: string
  oldestPendingAt: string
  items: ReviewQueueItem[]
}

/** Response for `GET /guestPhotos/v1/reviewer/queue`. */
export interface ReviewQueueResponse {
  brands: ReviewQueueBrand[]
}

/** Request body for `POST /guestPhotos/v1/reviewer/{id}/decide`. */
export type ReviewDecisionRequest =
  | { action: 'accept' }
  | { action: 'reject'; reasonCode: GuestPhotoRejectionReason; note?: string }
  | { action: 'unsure'; note?: string }
